import { useState, useMemo, useEffect, useCallback } from "react";

/**
 * Paginates an array of items based on the current page and items per page.
 * @template T - The type of items in the array
 */
export const paginateArray = <T>(
  items: T[],
  pageNumber: number,
  itemsPerPage = 10
): T[] => {
  const validItemsPerPage = Math.max(1, itemsPerPage);
  const totalPages = Math.ceil(items.length / validItemsPerPage);

  if (pageNumber <= 0 || pageNumber > totalPages) {
    return [];
  }

  return items.slice(
    validItemsPerPage * (pageNumber - 1),
    validItemsPerPage * pageNumber
  );
};

export interface UsePaginationReturn<T> {
  paginatedData: T[];
  currentPage: number;
  totalPages: number;
  handlePageChange: (_page: number) => void;
  totalCount: number;
}

/**
 * Custom hook for managing pagination state.
 * @template T - The type of items in the data array
 */
export const usePagination = <T>(
  data: T[],
  itemsPerPage = 10
): UsePaginationReturn<T> => {
  const [currentPage, setCurrentPage] = useState(1);

  const validItemsPerPage = Math.max(1, itemsPerPage);

  const totalPages = useMemo(
    () =>
      data.length === 0
        ? 0
        : Math.max(1, Math.ceil(data.length / validItemsPerPage)),
    [data.length, validItemsPerPage]
  );

  const paginatedData = useMemo(
    () => paginateArray(data, currentPage, validItemsPerPage),
    [data, currentPage, validItemsPerPage]
  );

  const handlePageChange = useCallback((page: number): void => {
    setCurrentPage(page);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  return {
    paginatedData,
    currentPage,
    totalPages,
    handlePageChange,
    totalCount: data.length,
  };
};

/**
 * Calculates the appropriate page number after an item deletion.
 * If the current page becomes empty after deletion, returns the last valid page.
 * @param currentPage - The current page number (1-indexed)
 * @param totalCount - The total number of items before deletion
 * @param itemsPerPage - Number of items per page
 * @returns The page number to navigate to after deletion
 */
export const getPageAfterDeletion = (
  currentPage: number,
  totalCount: number,
  itemsPerPage: number
): number => {
  const remainingCount = totalCount - 1;
  
  if (remainingCount === 0) {
    return 1;
  }
  
  const totalPages = Math.ceil(remainingCount / itemsPerPage);
  
  if (currentPage > totalPages) {
    return totalPages;
  }
  
  return currentPage;
};
 